import React from 'react'
import Text from '@components/V3/Text'
import TextSkeleton from '@components/V3/Text/TextSkeleton'
import {TrackingV2GetOrderQuery} from '@data/__generated__/types.main'
import classnames from '@helpers/misc/classnames'
import isScheduledAndPending from '@page-components/TrackV2/helpers/isScheduledAndConfirming'
import useOrder from '@page-components/TrackV2/hooks/useOrder.main'
import useStatus, {TrackingStatus} from '@page-components/TrackV2/hooks/useStatus'
import {TFunction, useTranslation} from 'next-i18next'

const hasDifferentTextForDeliveryType = (status: TrackingStatus) => {
  return ['preparation'].includes(status)
}

export const StatusSkeleton = () => (
  <div>
    <TextSkeleton type="title" />
    <TextSkeleton type="caption" className="w-1/2" />
  </div>
)

const getTitle = (
  status: TrackingStatus,
  order: TrackingV2GetOrderQuery['order'],
  t: TFunction
) => {
  if (status === 'delivery' && order.delivery?.nearClientAt) {
    return t(`status.${status}.titleNearCustomer`)
  }

  if (isScheduledAndPending(status, order) && order.isConfirmed) {
    return t(`status.${status}.scheduledConfirmed`)
  }

  return t(`status.${status}.title`)
}

const getSubtitle = (
  status: TrackingStatus,
  order: TrackingV2GetOrderQuery['order'],
  t: TFunction
) => {
  if (hasDifferentTextForDeliveryType(status)) {
    return t(`status.${status}.subtitle-${order.deliveryType}`)
  }

  if (isScheduledAndPending(status, order) && order.isConfirmed) {
    return t(`status.${status}.scheduledConfirmedSubtitle`)
  }

  return t(`status.${status}.subtitle`)
}

export default function Status() {
  const {t} = useTranslation('tracking')
  const status = useStatus()
  const {order} = useOrder()
  return (
    <div
      className={classnames({
        'text-center': isScheduledAndPending(status, order)
      })}>
      <Text type="title">{getTitle(status, order, t)}</Text>
      <Text type="caption">{getSubtitle(status, order, t)}</Text>
    </div>
  )
}
